import React, { useState, useEffect } from 'react';
import { isValidWord, loadDictionary } from '../utils/dictionaryValidator';

interface WordSearchProps {
  board: string[][];
  onFoundWord: (positions: [number, number][]) => void;
  onClear: () => void;
}

const WordSearch: React.FC<WordSearchProps> = ({ board, onFoundWord, onClear }) => {
  const [searchWord, setSearchWord] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load dictionary when component mounts
  useEffect(() => {
    const initDictionary = async () => {
      setIsLoading(true);
      await loadDictionary();
      setIsLoading(false);
    };
    
    initDictionary();
  }, []);

  const directions = [
    [-1, -1], [-1, 0], [-1, 1],
    [0, -1],           [0, 1],
    [1, -1],  [1, 0],  [1, 1]
  ];

  const searchWord2 = (word: string): [number, number][][] => {
    const results: [number, number][][] = [];
    const rows = board.length;
    const cols = board[0].length;

    const dfs = (i: number, j: number, index: number, path: [number, number][]) => {
      if (index === word.length) {
        results.push([...path]);
        return;
      }

      for (const [dx, dy] of directions) {
        const newI = i + dx;
        const newJ = j + dy;

        if (
          newI >= 0 && newI < rows &&
          newJ >= 0 && newJ < cols &&
          board[newI][newJ].toLowerCase() === word[index].toLowerCase() &&
          !path.some(([x, y]) => x === newI && y === newJ)
        ) {
          path.push([newI, newJ]);
          dfs(newI, newJ, index + 1, path);
          path.pop();
        }
      }
    };

    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        if (board[i][j].toLowerCase() === word[0].toLowerCase()) {
          dfs(i, j, 1, [[i, j]]);
        }
      }
    }

    return results;
  };

  const handleSearch = () => {
    if (!searchWord.trim()) {
      setMessage('Please enter a word');
      return;
    }
    
    // Check if the word exists in the dictionary
    if (!isValidWord(searchWord)) {
      setMessage(`"${searchWord}" is not in the dictionary`);
      onClear();
      return;
    }
    
    const results = searchWord2(searchWord);
    if (results.length > 0) {
      setMessage(`Found "${searchWord}"!`);
      onFoundWord(results[0]);
    } else {
      setMessage(`"${searchWord}" not found on the board`);
      onClear();
    }
  };

  const handleClear = () => {
    setSearchWord('');
    setMessage('');
    onClear();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="word-search">
      <h3>Find Words on the Board</h3>
      <div className="search-controls">
        <input
          type="text"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter a word to search"
          disabled={isLoading}
        />
        <button onClick={handleSearch} disabled={isLoading}>
          {isLoading ? 'Loading...' : 'Search'}
        </button>
        <button onClick={handleClear} disabled={isLoading}>Clear</button>
      </div>
      {isLoading && <div className="loading-message">Loading dictionary...</div>}
      {message && <div className="search-message" style={{
        marginTop: '10px',
        padding: '8px',
        borderRadius: '4px',
        backgroundColor: message.includes('not') ? '#ffebee' : '#e8f5e9',
        color: message.includes('not') ? '#c62828' : '#2e7d32'
      }}>{message}</div>}
    </div>
  );
};

export default WordSearch;
